import React from 'react';
import { init, shutdown } from '@fullstory/browser';

interface UseFullStoryOptions {
  authenticated: boolean;
  fullstoryEnabled: boolean;
  environment: string;
}

/**
 * Hook to manage FullStory initialization and shutdown based on authentication state
 * and feature flags. Handles session ref internally.
 *
 * @param options - Configuration options for FullStory
 * @param options.authenticated - Whether the user is currently authenticated
 * @param options.fullstoryEnabled - Whether the organization has FullStory feature enabled
 * @param options.environment - Current environment (e.g., 'local', 'development', 'staging', 'production')
 *
 * @example
 * ```tsx
 * useFullStory({
 *   authenticated: auth.authenticated,
 *   fullstoryEnabled: auth?.organization?.features?.fullstory || false,
 *   environment: config.environment,
 * });
 * ```
 */
const useFullStory = ({ authenticated, fullstoryEnabled, environment }: UseFullStoryOptions) => {
  const sessionRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!fullstoryEnabled || environment !== 'production') return;

    if (authenticated) {
      init({ orgId: 'W543D' }, ({ sessionUrl }) => {
        sessionRef.current = sessionUrl;
      });
    } else if (sessionRef.current) {
      shutdown();
      sessionRef.current = null;
    }
  }, [authenticated, fullstoryEnabled, environment]);
};

export default useFullStory;
